// Turns <JOB>_olink_report.json (the olink-proteomics 2.0.0 pipeline's report,
// exactly as the model package's assemble_report() writes it) into what the
// results view draws. The shape is:
//
//   format             'patient_report_pipeline_v1'
//   current_findings   { [condition group]: [finding, ...] }
//   ten_year_risk      { [condition group]: [finding, ...] }
//   provenance         { declared_panel_platform, observed_protein_count,
//                        observed_coverage_fraction, panel_mapping, ... }
//   limitations        [text, ...]   (not shown by the webserver)
//
// finding = { disease, score (0..1), tier (low | moderate | high),
//             interpretation, protein_panel }
// protein_panel is null, or the reference-panel proteins behind a moderate or
// high finding: { protein_id, gene_name, contribution, z,
//                 reference_band: { mean, lower, upper }, flagged }.
//
// Anything that does not match stops with a ReportFormatError rather than
// being drawn half right.
import { BODY_SYSTEMS, SYSTEM_BY_CATEGORY } from './bodySystems';

export const REPORT_FORMAT = 'patient_report_pipeline_v1';

// The pipeline flags a panel protein when |z| > 1.5, so the typical range drawn
// on every protein scale is -1.5 to 1.5.
export const FLAG_Z = 1.5;

export const SECTIONS = Object.freeze([
  { key: 'current_findings', section: 'current' },
  { key: 'ten_year_risk', section: 'ten_year' },
]);
const TIERS = Object.freeze(['low', 'moderate', 'high']);
const STATUS_RANK = Object.freeze({ high: 0, moderate: 1, typical: 2 });
const HIGHLIGHT_LIMIT = 4;

export class ReportFormatError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ReportFormatError';
  }
}

function fail(message) {
  throw new ReportFormatError(message);
}

function number(value, label) {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') {
    fail(`${label} is missing.`);
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) fail(`${label} is not a number.`);
  return parsed;
}

function text(value, label) {
  const parsed = typeof value === 'string' ? value.trim() : '';
  if (!parsed) fail(`${label} is missing.`);
  return parsed;
}

function bool(value, label) {
  if (value === true || value === false) return value;
  return fail(`${label} is not true or false.`);
}

function count(value, label) {
  const parsed = number(value, label);
  if (!Number.isSafeInteger(parsed) || parsed < 0) fail(`${label} is not a whole number.`);
  return parsed;
}

// The model scores from 0 to 1; everything on screen shows 0 to 100, rounded
// the same way as the PDF report.
export function scoreOutOf100(score) {
  return Math.round(score * 100);
}

function parseProtein(protein, where) {
  if (!protein || typeof protein !== 'object') fail(`${where} has an unreadable protein.`);
  const band = protein.reference_band;
  if (!band || typeof band !== 'object') fail(`${where} has a protein without a reference band.`);
  return Object.freeze({
    proteinId: text(protein.protein_id, `${where} protein id`),
    geneName: text(protein.gene_name, `${where} gene name`),
    z: number(protein.z, `${where} z-score`),
    contribution: number(protein.contribution, `${where} contribution`),
    band: Object.freeze({
      mean: number(band.mean, `${where} band mean`),
      lower: number(band.lower, `${where} band lower edge`),
      upper: number(band.upper, `${where} band upper edge`),
    }),
    flagged: bool(protein.flagged, `${where} flag`),
  });
}

function parseFinding(entry, section, category, system, order) {
  const where = `A ${section === 'current' ? 'current' : '10-year'} finding in "${category}"`;
  if (!entry || typeof entry !== 'object') fail(`${where} is unreadable.`);
  const score = number(entry.score, `${where} score`);
  if (score < 0 || score > 1) fail(`${where} has a score outside 0 to 1.`);
  if (!TIERS.includes(entry.tier)) fail(`${where} has an unknown tier (${String(entry.tier)}).`);
  const panel = entry.protein_panel;
  if (panel !== null && panel !== undefined && !Array.isArray(panel)) fail(`${where} has an unreadable protein panel.`);
  const disease = text(entry.disease, `${where} name`);
  return Object.freeze({
    id: `${section}-${order}`,
    order,
    section,
    category,
    system: system.code,
    disease,
    score,
    score100: scoreOutOf100(score),
    tier: entry.tier,
    interpretation: text(entry.interpretation, `${where} interpretation`),
    hasPanel: Array.isArray(panel),
    proteins: Object.freeze((panel || []).map((protein) => parseProtein(protein, `The panel for ${disease}`))),
  });
}

function byScore(a, b) {
  return (b.score - a.score) || (a.order - b.order);
}

function tierCounts(findings) {
  const counts = { high: 0, moderate: 0, low: 0 };
  findings.forEach((finding) => { counts[finding.tier] += 1; });
  return Object.freeze(counts);
}

// Within a section, body systems with moderate or high findings come first,
// led by their highest score; the rest keep the wheel's order.
function groupSection(findings) {
  const groups = new Map();
  findings.forEach((finding) => {
    if (!groups.has(finding.system)) groups.set(finding.system, []);
    groups.get(finding.system).push(finding);
  });
  return Object.freeze([...groups.entries()].map(([code, rows]) => {
    const system = BODY_SYSTEMS.find((item) => item.code === code);
    const sorted = [...rows].sort(byScore);
    const notable = sorted.filter((finding) => finding.tier !== 'low');
    return Object.freeze({
      system,
      total: sorted.length,
      notable: Object.freeze(notable),
      low: Object.freeze(sorted.filter((finding) => finding.tier === 'low')),
      topScore: notable.length ? notable[0].score : null,
    });
  }).sort((a, b) => {
    if ((a.topScore === null) !== (b.topScore === null)) return a.topScore === null ? 1 : -1;
    if (a.topScore !== null && a.topScore !== b.topScore) return b.topScore - a.topScore;
    return a.system.order - b.system.order;
  }));
}

// One entry per body system in the results: whether anything there is worth
// discussing, its strongest such finding, and how many other conditions there
// are worth discussing too.
function bodySystems(findings) {
  return Object.freeze(BODY_SYSTEMS.map((system) => {
    const rows = findings.filter((finding) => finding.system === system.code);
    if (!rows.length) return null;
    const notable = rows.filter((finding) => finding.tier !== 'low').sort(byScore);
    const top = notable[0] || null;
    const others = new Set(notable.map((finding) => finding.disease));
    if (top) others.delete(top.disease);
    let status = 'typical';
    if (notable.some((finding) => finding.tier === 'high')) status = 'high';
    else if (notable.length) status = 'moderate';
    return Object.freeze({
      ...system,
      status,
      top,
      others: others.size,
      notableCount: notable.length,
      total: rows.length,
    });
  }).filter(Boolean));
}

// Systems worth discussing first, strongest first, then the typical ones.
export function systemsByAttention(systems) {
  return [...systems].sort((a, b) => (STATUS_RANK[a.status] - STATUS_RANK[b.status])
    || ((b.top ? b.top.score : 0) - (a.top ? a.top.score : 0))
    || (a.order - b.order));
}

// The strongest moderate or high findings, one per condition name.
function highlights(findings) {
  const chosen = [];
  const names = new Set();
  [...findings].filter((finding) => finding.tier !== 'low').sort(byScore).forEach((finding) => {
    if (chosen.length >= HIGHLIGHT_LIMIT || names.has(finding.disease)) return;
    names.add(finding.disease);
    chosen.push(finding);
  });
  return Object.freeze(chosen);
}

function coverage(provenance) {
  if (!provenance || typeof provenance !== 'object') fail('The report has no provenance section.');
  const fraction = number(provenance.observed_coverage_fraction, 'The coverage fraction');
  if (fraction < 0 || fraction > 1) fail('The coverage fraction is outside 0 to 1.');
  const mapping = provenance.panel_mapping;
  return Object.freeze({
    platform: text(provenance.declared_panel_platform, 'The panel platform'),
    observedProteinCount: count(provenance.observed_protein_count, 'The number of proteins used'),
    coverageFraction: fraction,
    // Written by the pipeline's panel adapter; null when a report lacks it.
    panelProteinCount: mapping ? count(mapping.external_panel_size, 'The panel size') : null,
    matchedProteinCount: mapping ? count(mapping.matched_count, 'The matched protein count') : null,
    modelProteinCount: mapping ? count(mapping.frozen_protein_count, 'The model protein count') : null,
  });
}

export function buildReport(report) {
  if (!report || typeof report !== 'object' || Array.isArray(report)) fail('The report file is empty or unreadable.');
  if (report.format !== REPORT_FORMAT) {
    fail(`This report format is not supported (${String(report.format || 'none')}).`);
  }
  const findings = [];
  SECTIONS.forEach(({ key, section }) => {
    const groups = report[key];
    if (!groups || typeof groups !== 'object' || Array.isArray(groups)) fail(`The report has no ${key} section.`);
    Object.entries(groups).forEach(([category, entries]) => {
      const system = SYSTEM_BY_CATEGORY.get(category);
      if (!system) fail(`The condition group "${category}" has no body system.`);
      if (!Array.isArray(entries)) fail(`The condition group "${category}" is unreadable.`);
      entries.forEach((entry) => {
        findings.push(parseFinding(entry, section, category, system, findings.length));
      });
    });
  });
  if (!findings.length) fail('The report has no findings.');

  const sections = {};
  const counts = {};
  SECTIONS.forEach(({ section }) => {
    const rows = findings.filter((finding) => finding.section === section);
    sections[section] = groupSection(rows);
    counts[section] = tierCounts(rows);
  });
  const systems = bodySystems(findings);
  return Object.freeze({
    findings: Object.freeze(findings),
    sections: Object.freeze(sections),
    counts: Object.freeze(counts),
    totals: Object.freeze({
      results: findings.length,
      notable: findings.filter((finding) => finding.tier !== 'low').length,
      conditions: new Set(findings.map((finding) => finding.disease)).size,
    }),
    systems,
    typicalSystems: systems.filter((system) => system.status === 'typical').length,
    highlights: highlights(findings),
    coverage: coverage(report.provenance),
  });
}

// Where a protein sits on the scale, for the words beside its bar.
export function proteinState(protein) {
  if (!protein.flagged) return 'typical';
  return protein.z > 0 ? 'above' : 'below';
}
